/**
 * When to ask for a session key, and — much more importantly — when not to.
 *
 * `lib/`-shaped: no React, no network, no `Date.now()`. One pure predicate over
 * (key, market clock), so every branch is testable without a DOM and the roll
 * rule is a unit test rather than a hope.
 *
 * ## The rule
 *
 * A wallet popup takes a user's attention for as long as it takes them to read
 * it. On a venue whose markets last 60 seconds and whose gap between them is
 * ~13 seconds, *when* the popup happens is a product decision with a wrong
 * answer:
 *
 * - **Never at a roll boundary.** Plan §Phase 5 is explicit: a popup at the
 *   boundary eats the whole 13-second gap, so the user comes back to a market
 *   that started without them. The forbidden zone is the resolution window plus
 *   the opening seconds of the next market.
 * - **At `T+20`.** Mid-window, the quietest moment a market has: the strike is
 *   long fixed, expiry is 40 seconds away, and nothing is about to happen.
 * - **On first ticket focus, not on Buy.** Registering when the user clicks Buy
 *   puts a modal between an intent and its execution, on a market with 47
 *   seconds of trading in it. Focus is the earliest honest signal of intent.
 *
 * ## Two thresholds, not one, and they are different things
 *
 * docs/03 §3.2 says re-register at `T_expiry(key) − 10 min`; plan §Phase 5 says
 * rotate at `T+20` when expiry is inside 120 s; docs/03 §5.2 says drop a key
 * that expires within 120 s. Those are not three rules in conflict — they are a
 * lead time and a floor:
 *
 * | | Meaning |
 * |---|---|
 * | `ROTATE_LEAD_MS` (10 min) | Start *looking* for a safe window. There are ~10 of them left, so one will land at a `T+20` with no roll near it. |
 * | `KEY_MIN_REMAINING_MS` (120 s) | Stop *using* the key. Below this a key can die inside the market being traded, and a rejection at `T+50` is one the user cannot act on. |
 *
 * Rotating on the lead time is what stops the floor ever being reached during a
 * market. If it is reached anyway — a tab asleep for nine minutes — the key is
 * dropped and the ticket says "Enable trading", which is a deliberate,
 * user-timed popup rather than an ambush.
 */

import { KEY_MIN_REMAINING_MS } from './types'

export { KEY_MIN_REMAINING_MS }

/** Ten minutes of lead time — docs/03 §3.2. */
export const ROTATE_LEAD_MS = 600_000

/** The rotation point inside a market, in seconds after the anchor. */
export const ROTATE_AT_S = 20

/**
 * How far either side of `T+20` still counts as "at T+20".
 *
 * Wide enough that a tab throttled to one-second timers still finds the window,
 * narrow enough that it cannot drift into the roll. `T+18…T+24` is entirely
 * inside the trading window on a 60-second market with a ~13-second gap.
 */
export const ROTATE_WINDOW_S = 4

/**
 * The forbidden zone, in seconds after the anchor.
 *
 * From expiry to the point the next market is comfortably open. Anchored on the
 * period rather than hardcoded to 60 so a config change to `period_seconds`
 * moves it. The tail extends past the boundary because the *next* market's first
 * seconds are the other half of the same 13 seconds — a popup at `T+62` is at
 * `T'+2`, which is no better.
 */
export const ROLL_GUARD_BEFORE_S = 6
export const ROLL_GUARD_AFTER_S = 8

export interface KeyTiming {
  /** `SessionKey.expires_at_ms`, or `null` when there is no key at all. */
  keyExpiresAtMs: number | null
  /** Server-estimated now, from `lib/clock` — never `Date.now()` (invariant 7). */
  nowMs: number
  /** The focused market's anchor, in seconds. */
  anchorS: number
  /** `config.market.period_seconds`, never the constant 60 (invariant 12). */
  periodS: number
}

export type KeyState =
  /** No key, or one so close to expiry it must not be used. Ticket shows "Enable trading". */
  | 'ABSENT'
  /** Usable, and not yet worth rotating. */
  | 'ARMED'
  /** Usable, but inside the lead time — rotate at the next safe window. */
  | 'ROTATE_DUE'

export function keyState(timing: KeyTiming): KeyState {
  const { keyExpiresAtMs, nowMs } = timing
  if (keyExpiresAtMs === null) return 'ABSENT'
  const remaining = keyExpiresAtMs - nowMs
  if (remaining <= KEY_MIN_REMAINING_MS) return 'ABSENT'
  if (remaining <= ROTATE_LEAD_MS) return 'ROTATE_DUE'
  return 'ARMED'
}

/** Seconds elapsed since the focused market's anchor. Negative before it. */
export function offsetInMarketS(timing: KeyTiming): number {
  return timing.nowMs / 1000 - timing.anchorS
}

/**
 * Is now inside the window a popup must never land in?
 *
 * `[expiry − 6s, expiry + 8s]`, unchanged by register §F24 — **but its reason
 * inverted.** It used to guard the ~13 seconds in which nothing was tradable,
 * where a popup would eat the whole gap. There is no gap: the successor opens at
 * `expiry`, so the same fourteen seconds are now the busiest of the minute — the
 * close of one market and the open of the next. A modal covering the screen
 * there is worse than it was, not better, so the guard is load-bearing for a
 * stronger reason than the one it was written for.
 *
 * Checked against the *current* offset rather than the current market id,
 * because at `T+63` the client may still be pointed at market N while the user
 * is already trading N+1.
 */
export function isRollBoundary(timing: KeyTiming): boolean {
  const t = offsetInMarketS(timing)
  const { periodS } = timing
  const intoPeriod = ((t % periodS) + periodS) % periodS
  return intoPeriod >= periodS - ROLL_GUARD_BEFORE_S || intoPeriod <= ROLL_GUARD_AFTER_S
}

/**
 * May the client raise a wallet popup *right now*, on its own initiative?
 *
 * Note the qualifier. This governs **automatic** rotation only. A user who
 * clicks "Enable trading" gets their popup immediately whatever this returns —
 * refusing a signature the user just asked for, because the clock says `T+58`,
 * would be the client second-guessing an explicit instruction. `isRollBoundary`
 * still drives the *copy* there ("this market expires in 4s"), so the user is
 * told what they are walking into rather than blocked from walking into it.
 */
export function mayAutoRotate(timing: KeyTiming): boolean {
  if (keyState(timing) !== 'ROTATE_DUE') return false
  if (isRollBoundary(timing)) return false

  const t = offsetInMarketS(timing)
  const { periodS } = timing
  const intoPeriod = ((t % periodS) + periodS) % periodS
  return Math.abs(intoPeriod - ROTATE_AT_S) <= ROTATE_WINDOW_S / 2
}

/**
 * When the next safe rotation window opens, in ms from now.
 *
 * Used to schedule the check rather than poll it. Returns `0` when now is
 * already inside one.
 */
export function msUntilNextRotationWindow(timing: KeyTiming): number {
  if (mayAutoRotate(timing)) return 0
  const { periodS } = timing
  const t = offsetInMarketS(timing)
  const intoPeriod = ((t % periodS) + periodS) % periodS
  const target = ROTATE_AT_S - ROTATE_WINDOW_S / 2
  const delta = target - intoPeriod
  return Math.round((delta >= 0 ? delta : delta + periodS) * 1000)
}
